import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ExpandedContentType, SettingToggle } from '../settings-types'
import { ToggleSwitch } from '@tendbot/ui'
import {
  getThemePreference,
  setThemePreference,
  type ThemePreference,
} from '../../../shared/utils/theme'

interface TogglesExpandedProps {
  content: Extract<ExpandedContentType, { type: 'toggles' }>
  toggles: Record<string, boolean>
  savingToggle: string | null
  onToggle: (toggleId: string, currentValue: boolean) => void
}

const I18N_LABEL_KEY: Record<string, { label: string; description: string }> = {
  'theme': { label: 'appearance.theme', description: 'appearance.themeDesc' },
  'bigger-fonts': { label: 'appearance.biggerFonts', description: 'appearance.biggerFontsDesc' },
}

const THEME_OPTIONS: ThemePreference[] = ['auto', 'light', 'dark']

function ThemeSegments() {
  const { t } = useTranslation('settings')
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference())

  const handleSelect = (next: ThemePreference) => {
    setPref(next)
    setThemePreference(next)
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('appearance.theme')}
      className="inline-flex rounded-full bg-slate-mid/30 p-0.5"
    >
      {THEME_OPTIONS.map((opt) => {
        const selected = pref === opt
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={(e) => {
              e.stopPropagation()
              handleSelect(opt)
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              selected
                ? 'bg-gold/20 text-gold'
                : 'text-pearl/70 hover:text-pearl'
            }`}
          >
            {t(`appearance.themeOption.${opt}`)}
          </button>
        )
      })}
    </div>
  )
}

export default function TogglesExpanded({ content, toggles, savingToggle, onToggle }: TogglesExpandedProps) {
  const { t } = useTranslation('settings')
  return (
    <div className="space-y-1">
      {content.settings?.map((setting: SettingToggle) => {
        const keys = I18N_LABEL_KEY[setting.id]
        const label = keys ? t(keys.label) : setting.label
        const description = keys ? t(keys.description) : setting.description
        return (
          <div key={setting.id} className="flex items-center justify-between gap-3 py-2">
            <div>
              <span className="text-sm text-pearl">{label}</span>
              {description && (
                <p className="text-xs text-pearl/50">{description}</p>
              )}
            </div>
            {setting.id === 'theme' ? (
              <ThemeSegments />
            ) : (
              <ToggleSwitch
                checked={(toggles[setting.id] ?? setting.value) as boolean}
                onChange={(e) => {
                  e?.stopPropagation()
                  onToggle(setting.id, (toggles[setting.id] ?? setting.value) as boolean)
                }}
                saving={savingToggle === setting.id}
              />
            )}
          </div>
        )
      })}

      {/* Additional settings */}
      {content.additionalSettings?.map((setting) => (
        <div key={setting.id} className="flex items-center justify-between py-2">
          <div>
            <span className="text-sm text-pearl">{setting.label}</span>
            {setting.description && (
              <p className="text-xs text-pearl/50">{setting.description}</p>
            )}
          </div>
          <span className="text-sm text-gold">{setting.value}</span>
        </div>
      ))}
    </div>
  )
}
